import axios from 'axios';
import { PUBLIC_API_URL } from '../config/api';
import { AxiosError } from 'axios';
import type { SavedQuery, CreateQueryRequest, UpdateQueryRequest, QueryExecutionResult } from '../types/query';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  columns?: string[];
  rowCount?: number;
  executionTime?: number;
  metadata?: {
    executionTime?: number;
  };
}

class QueryService {
  private handleError(error: any, defaultMessage: string): never {
    if (error) {
      const errorMessage = error.response?.data?.error || error.message || defaultMessage;
      throw new Error(errorMessage);
    }
    throw error instanceof Error ? error : new Error(defaultMessage);
  }

  async getQueries(): Promise<SavedQuery[]> {
    try {
      const response = await axios.get<ApiResponse<SavedQuery[]>>('/api/services');
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching queries:', error);
      this.handleError(error, 'Erro ao carregar queries');
    }
  }

  async getQueryById(id: string): Promise<SavedQuery | null> {
    try {
      const response = await axios.get<ApiResponse<SavedQuery>>(`/api/services/${id}`);
      return response.data.data || null;
    } catch (error) {
      if (error && error.response?.status === 404) {
        return null;
      }
      console.error('Error fetching query:', error);
      this.handleError(error, 'Erro ao carregar query');
    }
  }

  async getQueryBySlug(slug: string): Promise<SavedQuery | null> {
    try {
      const response = await axios.get<ApiResponse<SavedQuery>>(`/api/services/by-slug/${slug}`);
      return response.data.data || null;
    } catch (error) {
      if (error && error.response?.status === 404) {
        return null;
      }
      console.error('Error fetching query by slug:', error);
      this.handleError(error, 'Erro ao carregar query');
    }
  }

  async createQuery(queryData: CreateQueryRequest): Promise<SavedQuery> {
    try {
      const payload = {
        name: queryData.name,
        description: queryData.description || null,
        sql_query: queryData.sql_query,
        endpoint_slug: queryData.endpoint_slug || queryData.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
        selected_columns: queryData.selected_columns || [],
        filter_columns: queryData.filter_columns || [],
        column_distributor: queryData.column_distributor || null,
        column_industry: queryData.column_industry || null,
        availability_type: queryData.availability_type || 'api',
        documentation_description: queryData.documentation_description || null,
        is_active: queryData.is_active ?? true,
        automation_enabled: queryData.automation_enabled ?? true,
        automation_interval: queryData.automation_interval || '24h',
        integration_solace_enabled: queryData.integration_solace_enabled ?? false,
        integration_solace_broker_url: queryData.integration_solace_broker_url || null,
        integration_solace_vpn_name: queryData.integration_solace_vpn_name || null,
        integration_solace_queue_name: queryData.integration_solace_queue_name || null,
        integration_solace_username: queryData.integration_solace_username || null,
        integration_solace_password: queryData.integration_solace_password || null,
        integration_solace_interval: queryData.integration_solace_interval || null,
        integration_sap_btp_enabled: queryData.integration_sap_btp_enabled ?? false,
        integration_sap_btp_service_url: queryData.integration_sap_btp_service_url || null,
        integration_sap_btp_client_id: queryData.integration_sap_btp_client_id || null,
        integration_sap_btp_client_secret: queryData.integration_sap_btp_client_secret || null,
        integration_sap_btp_token_url: queryData.integration_sap_btp_token_url || null,
        integration_sap_btp_api_path: queryData.integration_sap_btp_api_path || null,
        integration_sap_btp_interval: queryData.integration_sap_btp_interval || null
      };

      const response = await axios.post<ApiResponse<{ id?: number }>>('/api/services', payload);

      if (response.data.success && response.data.data?.id) {
        const created = await this.getQueryById(String(response.data.data.id));
        if (created) return created;
      }

      const queries = await this.getQueries();
      const newQuery = queries.find(q => q.name === queryData.name);

      if (!newQuery) {
        throw new Error('Query criada mas nao encontrada na lista');
      }

      return newQuery;
    } catch (error) {
      console.error('Error creating query:', error);
      this.handleError(error, 'Erro ao criar query');
    }
  }

  async updateQuery(id: string, queryData: UpdateQueryRequest): Promise<SavedQuery> {
    try {
      const updatePayload: Record<string, unknown> = { ...queryData };

      if (queryData.automation_enabled === false) {
        updatePayload.automation_interval = null;
      }

      if (queryData.integration_solace_enabled === false) {
        updatePayload.integration_solace_broker_url = null;
        updatePayload.integration_solace_vpn_name = null;
        updatePayload.integration_solace_queue_name = null;
        updatePayload.integration_solace_username = null;
        updatePayload.integration_solace_password = null;
        updatePayload.integration_solace_interval = null;
      }

      if (queryData.integration_sap_btp_enabled === false) {
        updatePayload.integration_sap_btp_service_url = null;
        updatePayload.integration_sap_btp_client_id = null;
        updatePayload.integration_sap_btp_client_secret = null;
        updatePayload.integration_sap_btp_token_url = null;
        updatePayload.integration_sap_btp_api_path = null;
        updatePayload.integration_sap_btp_interval = null;
      }

      await axios.put(`/api/services/${id}`, updatePayload);

      const updated = await this.getQueryById(id);

      if (!updated) {
        throw new Error('Query atualizada mas nao encontrada');
      }

      return updated;
    } catch (error) {
      console.error('Error updating query:', error);
      this.handleError(error, 'Erro ao atualizar query');
    }
  }

  async deleteQuery(id: string): Promise<void> {
    try {
      await axios.delete(`/api/services/${id}`);
    } catch (error) {
      console.error('Error deleting query:', error);
      this.handleError(error, 'Erro ao excluir query');
    }
  }

  async toggleQueryStatus(id: string): Promise<boolean> {
    try {
      const response = await axios.patch<ApiResponse<{ is_active?: boolean }>>(
        `/api/services/${id}/toggle-status`
      );
      return response.data.data?.is_active ?? false;
    } catch (error) {
      console.error('Error toggling query status:', error);
      this.handleError(error, 'Erro ao alternar status do servico');
    }
  }

  async executeQuery(query: SavedQuery): Promise<QueryExecutionResult> {
    try {
      const response = await axios.post<ApiResponse<Record<string, unknown>[]>>(
        `/api/services/${query.id}/execute`,
        { filters: {} }
      );

      if (!response.data.success) {
        return {
          success: false,
          data: [],
          columns: [],
          rowCount: 0,
          executionTime: 0,
          error: response.data.error || 'Erro ao executar query'
        };
      }

      const data = response.data.data || [];
      const columns = data.length > 0 ? Object.keys(data[0]) : [];

      return {
        success: true,
        data,
        columns,
        rowCount: data.length,
        executionTime: response.data.metadata?.executionTime || 0
      };
    } catch (error) {
      console.error('Error executing query:', error);
      return {
        success: false,
        data: [],
        columns: [],
        rowCount: 0,
        executionTime: 0,
        error: error instanceof Error ? error.message : 'Erro ao executar query'
      };
    }
  }

  async executeRawQuery(sqlQuery: string, selectedColumns?: string[]): Promise<QueryExecutionResult> {
    try {
      const response = await axios.post<ApiResponse<Record<string, unknown>[]>>(
        '/api/services/test',
        { sql_query: sqlQuery }
      );

      if (!response.data.success) {
        return {
          success: false,
          data: [],
          columns: [],
          rowCount: 0,
          executionTime: 0,
          error: response.data.error || 'Erro ao executar query'
        };
      }

      let data = response.data.data || [];

      if (selectedColumns && selectedColumns.length > 0) {
        data = data.map((row: Record<string, unknown>) => {
          const filtered: Record<string, unknown> = {};
          selectedColumns.forEach(col => {
            if (col in row) {
              filtered[col] = row[col];
            }
          });
          return filtered;
        });
      }

      const columns = data.length > 0 ? Object.keys(data[0]) : (response.data.columns || []);

      return {
        success: true,
        data,
        columns,
        rowCount: response.data.rowCount || data.length,
        executionTime: response.data.executionTime || 0
      };
    } catch (error) {
      console.error('Error executing raw query:', error);
      return {
        success: false,
        data: [],
        columns: [],
        rowCount: 0,
        executionTime: 0,
        error: error instanceof Error ? error.message : 'Erro ao executar query'
      };
    }
  }

  generateEndpointUrl(slug: string): string {
    return `${PUBLIC_API_URL}/services/by-slug/${slug}`;
  }

  validateSlug(slug: string): boolean {
    const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
    return slugRegex.test(slug);
  }

  slugify(text: string): string {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }
}

export const queryService = new QueryService();
